import type { StoreType, RootState } from 'src/store'

type Selector = (state: RootState) => unknown

class StoreListener {
  constructor(
    private readonly selectors: Map<Selector, unknown>,
    private readonly onChange: (...values: unknown[]) => void,
  ) {}

  run(state: RootState) {
    let changed = false
    this.selectors.forEach((prev, selector) => {
      const newValue = selector(state)
      if (newValue !== prev) {
        changed = true
        this.selectors.set(selector, newValue)
      }
    })
    if (changed) {
      this.onChange(...Array.from(this.selectors.values()))
    }
  }
}

type Values<S extends readonly Selector[]> = { [K in keyof S]: S[K] extends (state: RootState) => infer R ? R : never }

/**
 * Calls a handler when any of the selected values changes (by reference).
 * Handlers run one store update at a time, in the order they were added.
 */
export class StoreObserver {

  private listeners: StoreListener[] = []

  private running: Promise<void> = Promise.resolve()

  private readonly unsubscribe: () => void

  constructor(private readonly store: StoreType) {
    this.unsubscribe = this.store.subscribe(async () => {
      const state = { ...this.store.getState() }
      await this.running
      this.running = new Promise((resolve) => {
        this.listeners.forEach((listener) => listener.run(state))
        resolve()
      })
    })
  }

  /** With immediate the handler also runs right away with the current values */
  listen<S extends readonly [Selector, ...Selector[]]>(
    selectors: S,
    onChange: (...values: Values<S>) => void | Promise<void>,
    immediate = false,
  ) {
    const selectorsMap = new Map<Selector, unknown>()
    selectors.forEach((selector) => {
      selectorsMap.set(selector, immediate ? undefined : selector(this.store.getState()))
    })
    const listener = new StoreListener(selectorsMap, onChange as (...values: unknown[]) => void)
    this.listeners.push(listener)
    if (immediate) {
      listener.run(this.store.getState())
    }
  }

  destroy() {
    this.unsubscribe()
  }
}
